import { combineEpics } from 'redux-observable'
import { Observable } from 'rxjs/Observable'

import { ALL_COMPANIES_OPTION } from 'utils/partnerUtils'
import { toCamelCase } from 'utils/strings'

import {
  DELIVERY,
  PARTICIPANTS_ALL_CATEGORY,
  PARTICIPANTS_EXCLUDE_FILTER_VALUES,
  PARTICIPANTS_NAMES_CATEGORY,
  PARTICIPANTS_PER_PAGE,
} from 'containers/AwarenessCenter/constants'
import { selectCompanyFilter } from 'containers/PartnerOptions/selectors'

import { addToast } from '../../../Toasts/actions'

import {
  createGroupAutonomousConfigMemberError,
  createGroupAutonomousConfigMemberSuccess,
  deleteGroupAutonomousConfigMemberError,
  deleteGroupAutonomousConfigMemberSuccess,
  fetchAutonomousScheduleError,
  fetchAutonomousScheduleSuccess,
  fetchParticipantsDataError,
  fetchParticipantsDataSuccess,
  getGroupAutonomousConfigMemberCandidatesError,
  getGroupAutonomousConfigMemberCandidatesSuccess,
  getGroupAutonomousConfigMemberError,
  getGroupAutonomousConfigMemberSuccess,
  getGroupAutonomousScheduleError,
  getGroupAutonomousScheduleSuccess,
  getParticipantsError,
  getParticipantsInfoError,
  getParticipantsInfoSuccess,
  getParticipantsSuccess,
  saveGroupAutonomousScheduleError,
  saveGroupAutonomousScheduleSuccess,
  searchParticipantsInfoError,
  searchParticipantsInfoSuccess,
  submitAutonomousScheduleError,
  submitAutonomousScheduleSuccess,
  updateGroupAutonomousConfigMemberError,
  updateGroupAutonomousConfigMemberSuccess,
  updateParticipantsNumbersError,
  updateParticipantsNumbersSuccess,
} from './actions'
import {
  CHANGE_MSP_PARTICIPANTS_MODAL_PAGE,
  CHANGE_PARTICIPANTS_SELECTION,
  CHANGE_PARTICIPANTS_TOTAL_SELECTION,
  CREATE_GROUP_AUTONOMOUS_CONFIG_MEMBER_ASYNC,
  DELETE_GROUP_AUTONOMOUS_CONFIG_MEMBER_ASYNC,
  FETCH_AUTONOMOUS_SCHEDULE_ASYNC,
  FETCH_PARTICIPANTS_DATA_ASYNC,
  GET_GROUP_AUTONOMOUS_CONFIG_MEMBER_ASYNC,
  GET_GROUP_AUTONOMOUS_CONFIG_MEMBER_CANDIDATES_ASYNC,
  GET_GROUP_AUTONOMOUS_SCHEDULE_ASYNC,
  GET_PARTICIPANTS_ASYNC,
  GET_PARTICIPANTS_INFO_ASYNC,
  MEMBERS_PER_PAGE,
  SAVE_GROUP_AUTONOMOUS_SCHEDULE_ASYNC,
  SEARCH_PARTICIPANTS_INFO_ASYNC,
  SUBMIT_AUTONOMOUS_SCHEDULE_ASYNC,
  UPDATE_GROUP_AUTONOMOUS_CONFIG_MEMBER_ASYNC,
} from './constants'
import { selectMembers, selectParticipants } from './selectors'

const fetchAutonomousScheduleEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(FETCH_AUTONOMOUS_SCHEDULE_ASYNC.INITIATE).switchMap(() =>
    api.awarenessCenter
      .fetchAutonomousSchedule(store.getState().partnerOptions.selectedCompany?.value)
      .map((response) => fetchAutonomousScheduleSuccess(response))
      .catch((error) => Observable.of(epicsErrorHandler(fetchAutonomousScheduleError, error, [])))
      .takeUntil(action$.ofType(FETCH_AUTONOMOUS_SCHEDULE_ASYNC.CANCEL))
  )

const getGroupAutonomousScheduleEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(GET_GROUP_AUTONOMOUS_SCHEDULE_ASYNC.INITIATE).switchMap(() =>
    api.awarenessCenter
      .getGroupAutonomousSchedule()
      .map((response) => getGroupAutonomousScheduleSuccess(response))
      .catch((error) =>
        Observable.of(epicsErrorHandler(getGroupAutonomousScheduleError, error, []))
      )
      .takeUntil(action$.ofType(GET_GROUP_AUTONOMOUS_SCHEDULE_ASYNC.CANCEL))
  )

const getGroupAutonomousConfigMemberEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$
    .ofType(
      GET_GROUP_AUTONOMOUS_CONFIG_MEMBER_ASYNC.INITIATE,
      CREATE_GROUP_AUTONOMOUS_CONFIG_MEMBER_ASYNC.SUCCESS,
      DELETE_GROUP_AUTONOMOUS_CONFIG_MEMBER_ASYNC.SUCCESS,
      CHANGE_MSP_PARTICIPANTS_MODAL_PAGE,
      UPDATE_GROUP_AUTONOMOUS_CONFIG_MEMBER_ASYNC.SUCCESS
    )
    .filter((action) => {
      if (
        action.type === CREATE_GROUP_AUTONOMOUS_CONFIG_MEMBER_ASYNC.INITIATE &&
        !store.getState().awarenessCenter.autonomousScheduler.isMemberRemoving
      ) {
        return false
      } else {
        return true
      }
    })
    .switchMap((action) => {
      const successCallback =
        action.type === UPDATE_GROUP_AUTONOMOUS_CONFIG_MEMBER_ASYNC.SUCCESS
          ? updateParticipantsNumbersSuccess
          : getGroupAutonomousConfigMemberSuccess
      const errorCallback =
        action.type === UPDATE_GROUP_AUTONOMOUS_CONFIG_MEMBER_ASYNC.SEARCH_PARTICIPANTS_INFO_ASYNC
          ? updateParticipantsNumbersError
          : getGroupAutonomousConfigMemberError

      return api.awarenessCenter
        .getGroupAutonomousConfigMember(selectMembers(store.getState()).page, MEMBERS_PER_PAGE)
        .map((response) => successCallback(response))
        .catch((error) => Observable.of(epicsErrorHandler(errorCallback, error, [404])))
        .takeUntil(action$.ofType(GET_GROUP_AUTONOMOUS_CONFIG_MEMBER_ASYNC.CANCEL))
    })

const getGroupAutonomousConfigMemberCandidatesEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(GET_GROUP_AUTONOMOUS_CONFIG_MEMBER_CANDIDATES_ASYNC.INITIATE).switchMap(() =>
    api.awarenessCenter
      .getGroupAutonomousConfigMemberCandidates()
      .map((response) => getGroupAutonomousConfigMemberCandidatesSuccess(response))
      .catch((error) =>
        Observable.of(epicsErrorHandler(getGroupAutonomousConfigMemberCandidatesError, error, []))
      )
      .takeUntil(action$.ofType(GET_GROUP_AUTONOMOUS_CONFIG_MEMBER_CANDIDATES_ASYNC.CANCEL))
  )

const createGroupAutonomousConfigMemberEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(CREATE_GROUP_AUTONOMOUS_CONFIG_MEMBER_ASYNC.INITIATE).switchMap((action) =>
    api.awarenessCenter
      .createGroupAutonomousConfigMember({
        companyId: action.payload.companyId,
        participantsChoices: {
          [PARTICIPANTS_ALL_CATEGORY]: [PARTICIPANTS_ALL_CATEGORY],
        },
      })
      .map((response) => createGroupAutonomousConfigMemberSuccess(response))
      .catch((error) =>
        Observable.of(epicsErrorHandler(createGroupAutonomousConfigMemberError, error, []))
      )
      .takeUntil(action$.ofType(CREATE_GROUP_AUTONOMOUS_CONFIG_MEMBER_ASYNC.CANCEL))
  )

const deleteGroupAutonomousConfigMemberEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(DELETE_GROUP_AUTONOMOUS_CONFIG_MEMBER_ASYNC.INITIATE).switchMap((action) =>
    api.awarenessCenter
      .deleteGroupAutonomousConfigMember(action.payload.companyId)
      .map((response) => deleteGroupAutonomousConfigMemberSuccess(response))
      .catch((error) =>
        Observable.of(epicsErrorHandler(deleteGroupAutonomousConfigMemberError, error, []))
      )
      .takeUntil(action$.ofType(DELETE_GROUP_AUTONOMOUS_CONFIG_MEMBER_ASYNC.CANCEL))
  )

const updateGroupAutonomousConfigMemberEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(UPDATE_GROUP_AUTONOMOUS_CONFIG_MEMBER_ASYNC.INITIATE).switchMap((action) =>
    api.awarenessCenter
      .updateGroupAutonomousConfigMember(action.payload.companyId, action.payload.data)
      .map((response) => updateGroupAutonomousConfigMemberSuccess(response))
      .catch((error) =>
        Observable.of(epicsErrorHandler(updateGroupAutonomousConfigMemberError, error, []))
      )
      .takeUntil(action$.ofType(UPDATE_GROUP_AUTONOMOUS_CONFIG_MEMBER_ASYNC.CANCEL))
  )

const saveGroupAutonomousScheduleEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(SAVE_GROUP_AUTONOMOUS_SCHEDULE_ASYNC.INITIATE).switchMap((action) =>
    api.awarenessCenter
      .saveGroupAutonomousSchedule({
        months: action.payload.months,
        forceSave: action.payload.forceSave,
        membersSelectionData: {
          allItems: action.payload.allItems,
          excludeIds: action.payload.excludeIds,
        },
      })
      .mergeMap((response) =>
        Observable.of(
          saveGroupAutonomousScheduleSuccess(response),
          addToast({
            message: action.payload.months.length
              ? 'Autonomous campaign successfully scheduled!'
              : 'Changes successfully saved!',
          })
        )
      )
      .catch((error) =>
        Observable.of(epicsErrorHandler(saveGroupAutonomousScheduleError, error, [400]))
      )
      .takeUntil(action$.ofType(SAVE_GROUP_AUTONOMOUS_SCHEDULE_ASYNC.CANCEL))
  )

const submitAutonomousScheduleEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(SUBMIT_AUTONOMOUS_SCHEDULE_ASYNC.INITIATE).switchMap((action) =>
    api.awarenessCenter
      .submitAutonomousSchedule(
        store.getState().partnerOptions.selectedCompany?.value,
        action.payload.months,
        selectParticipants(store.getState()).included,
        selectParticipants(store.getState()).excluded
      )
      .mergeMap((response) =>
        Observable.of(
          submitAutonomousScheduleSuccess(response, action.payload.months),
          addToast({
            message: action.payload.months.length
              ? 'Autonomous campaign successfully scheduled!'
              : 'Changes successfully saved!',
          })
        )
      )
      .catch((error) => Observable.of(epicsErrorHandler(submitAutonomousScheduleError, error, [])))
      .takeUntil(action$.ofType(SUBMIT_AUTONOMOUS_SCHEDULE_ASYNC.CANCEL))
  )

const getParticipantsInfoEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$
    .ofType(
      FETCH_AUTONOMOUS_SCHEDULE_ASYNC.SUCCESS,
      GET_PARTICIPANTS_INFO_ASYNC.INITIATE,
      CHANGE_PARTICIPANTS_SELECTION,
      CHANGE_PARTICIPANTS_TOTAL_SELECTION
    )
    .filter((action) => {
      const companyFilter = selectCompanyFilter(store.getState())
      const isAllCompanySelected = companyFilter?.label === ALL_COMPANIES_OPTION.label

      if (
        isAllCompanySelected &&
        [CHANGE_PARTICIPANTS_SELECTION, CHANGE_PARTICIPANTS_TOTAL_SELECTION].includes(action.type)
      ) {
        return false
      } else {
        return true
      }
    })
    .mergeMap((action) => {
      const companyFilter = selectCompanyFilter(store.getState())
      const isAllCompanySelected = companyFilter?.label === ALL_COMPANIES_OPTION.label
      const companyId = isAllCompanySelected
        ? action.payload.companyId
        : store.getState().partnerOptions.selectedCompany?.value

      return api.wizardCampaign
        .getParticipantsInfo(companyId, {
          deliveryType: DELIVERY.EMAIL,
          groups: selectParticipants(store.getState()).included,
          excludedGroups: selectParticipants(store.getState()).excluded,
        })
        .mergeMap((response) =>
          Observable.of(getParticipantsInfoSuccess(response.response, action.payload?.index))
        )
        .catch((error) => Observable.of(epicsErrorHandler(getParticipantsInfoError, error, [400])))
        .takeUntil(action$.ofType(GET_PARTICIPANTS_INFO_ASYNC.CANCEL))
    })

const searchParticipantsInfoEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(SEARCH_PARTICIPANTS_INFO_ASYNC.INITIATE).mergeMap((action) => {
    const companyFilter = selectCompanyFilter(store.getState())
    const isAllCompanySelected = companyFilter?.label === ALL_COMPANIES_OPTION.label
    const companyId = isAllCompanySelected
      ? action.payload.companyId
      : store.getState().partnerOptions.selectedCompany?.value

    return api.wizardCampaign
      .getParticipantsInfo(companyId, {
        searchFilter: action.payload.value,
        deliveryType: DELIVERY.EMAIL,
        groups: selectParticipants(store.getState()).included,
        excludedGroups: selectParticipants(store.getState()).excluded,
      })
      .mergeMap((response) =>
        Observable.of(searchParticipantsInfoSuccess(response.response, action.payload?.index))
      )
      .catch((error) => Observable.of(epicsErrorHandler(searchParticipantsInfoError, error, [400])))
      .takeUntil(action$.ofType(SEARCH_PARTICIPANTS_INFO_ASYNC.CANCEL))
  })

// TODO : remove when containers\Settings\Notifications\EndUser\Editor will not have this dependency
const fetchParticipantsDataEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$
    .ofType(FETCH_AUTONOMOUS_SCHEDULE_ASYNC.SUCCESS, FETCH_PARTICIPANTS_DATA_ASYNC.INITIATE)
    .mergeMap((action) =>
      api.wizardCampaign
        .getSelectedParticipantsDetails(store.getState().partnerOptions.selectedCompany?.value, {
          deliveryType: DELIVERY.EMAIL,
          groups: action.payload.data.participants_choices || action.payload.data.groups,
          excludedGroups:
            action.payload.data.participants_exclude_choices || action.payload.data.excludedGroups,
        })
        .mergeMap((response) => Observable.of(fetchParticipantsDataSuccess(response.response)))
        .catch((error) =>
          Observable.of(epicsErrorHandler(fetchParticipantsDataError, error, [400]))
        )
        .takeUntil(action$.ofType(FETCH_PARTICIPANTS_DATA_ASYNC.CANCEL))
    )

const getParticipantsEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$
    .ofType(GET_PARTICIPANTS_ASYNC.INITIATE, GET_PARTICIPANTS_INFO_ASYNC.SUCCESS)
    .switchMap((action) => {
      const companyFilter = selectCompanyFilter(store.getState())
      const isAllCompanySelected = companyFilter?.label === ALL_COMPANIES_OPTION.label
      const companyId = isAllCompanySelected
        ? action.payload.companyId
        : store.getState().partnerOptions.selectedCompany?.value
      let showIncluded
      let showExcluded
      if (action.type === GET_PARTICIPANTS_INFO_ASYNC.SUCCESS) {
        showIncluded = true
        showExcluded = false
      } else {
        // modal case
        const excludeFilter = selectParticipants(store.getState()).excludeFilter
        showIncluded =
          excludeFilter === PARTICIPANTS_EXCLUDE_FILTER_VALUES.ALL ||
          excludeFilter === PARTICIPANTS_EXCLUDE_FILTER_VALUES.INCLUDED
        showExcluded =
          excludeFilter === PARTICIPANTS_EXCLUDE_FILTER_VALUES.ALL ||
          excludeFilter === PARTICIPANTS_EXCLUDE_FILTER_VALUES.EXCLUDED
      }

      const participants = selectParticipants(store.getState())
      const include = {
        allCompany: false,
        show: showIncluded,
      }
      const exclude = {
        show: showExcluded,
      }

      Object.keys(participants.included).forEach((key) => {
        const formattedKey = toCamelCase(key)

        if (key === PARTICIPANTS_ALL_CATEGORY) {
          include.allCompany = true
        } else if (key === PARTICIPANTS_NAMES_CATEGORY) {
          include.emails = participants.included[key]
        } else {
          include[formattedKey] = participants.included[key]
        }
      })

      Object.keys(participants.excluded).forEach((key) => {
        const formattedKey = toCamelCase(key)

        if (key === PARTICIPANTS_NAMES_CATEGORY) {
          exclude.emails = participants.excluded[key]
        } else {
          exclude[formattedKey] = participants.excluded[key]
        }
      })

      if (include.emails?.length === 0) delete include.emails
      if (exclude.emails?.length === 0) delete exclude.emails

      return api.wizardCampaign
        .getParticipants(
          companyId,
          {
            search: selectParticipants(store.getState()).modalSearch,
            deliveryType: DELIVERY.EMAIL,
            include,
            exclude,
          },
          selectParticipants(store.getState()).page,
          PARTICIPANTS_PER_PAGE
        )
        .mergeMap((response) => Observable.of(getParticipantsSuccess(response.response)))
        .catch((error) => Observable.of(epicsErrorHandler(getParticipantsError, error, [400])))
        .takeUntil(action$.ofType(GET_PARTICIPANTS_ASYNC.CANCEL))
    })

export default combineEpics(
  fetchAutonomousScheduleEpic,
  submitAutonomousScheduleEpic,
  getParticipantsInfoEpic,
  getParticipantsEpic,
  searchParticipantsInfoEpic,
  fetchParticipantsDataEpic,
  getGroupAutonomousScheduleEpic,
  getGroupAutonomousConfigMemberEpic,
  getGroupAutonomousConfigMemberCandidatesEpic,
  createGroupAutonomousConfigMemberEpic,
  deleteGroupAutonomousConfigMemberEpic,
  updateGroupAutonomousConfigMemberEpic,
  saveGroupAutonomousScheduleEpic
)
